import { ReactNode, useEffect } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import * as authTypes from "./constants/authTypes";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Unauthorised from "./components/Unauthorised";

interface Auth0Config {
  domain: string;
  clientId: string;
  audience: string;
  orgId: string;
}

interface Auth0WrapperProps {
  children: ReactNode;
}

const {
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_AUDIENCE,
  REACT_APP_AUTH0_ORG_ID,
} = process.env;

if (
  !REACT_APP_AUTH0_DOMAIN ||
  !REACT_APP_AUTH0_CLIENT_ID ||
  !REACT_APP_AUTH0_AUDIENCE ||
  !REACT_APP_AUTH0_ORG_ID
) {
  throw new Error("Missing config");
}

const auth0Config: Auth0Config = {
  domain: REACT_APP_AUTH0_DOMAIN,
  clientId: REACT_APP_AUTH0_CLIENT_ID,
  audience: REACT_APP_AUTH0_AUDIENCE,
  orgId: REACT_APP_AUTH0_ORG_ID,
};

const ProtectedContent = ({ children }: Auth0WrapperProps) => {
  const { isLoading, isAuthenticated } = useAuth0();
  const { login } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login();
    }
  }, [isLoading, isAuthenticated]);

  if (isLoading || !isAuthenticated) {
    return <></>;
  }

  return <>{children}</>;
};

const onRedirectCallback = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

const Auth0Wrapper = ({ children }: Auth0WrapperProps) => {
  return (
    <BrowserRouter>
      <Auth0Provider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: auth0Config.audience,
          organization: auth0Config.orgId,
        }}
        onRedirectCallback={onRedirectCallback}
        useRefreshTokens={true}
        cacheLocation="memory"
      >
        <AuthProvider authType={authTypes.AUTH0}>
          <Routes>
            <Route path="/unauthorised" element={<Unauthorised />} />
            <Route path="/" element={<ProtectedContent>{children}</ProtectedContent>} />
          </Routes>
        </AuthProvider>
      </Auth0Provider>
    </BrowserRouter>
  );
};

export default Auth0Wrapper;
